import { loadOcrAssets } from './ocrModelCache';

export const SDK_URL = 'https://cdn.jsdelivr.net/npm/@paddleocr/paddleocr-js@0.4.2/+esm';
export const MODEL_NAME = 'PP-OCRv6 Small · tr · WASM';
export const MODEL_VERSION = '@paddleocr/paddleocr-js@0.4.2 / PP-OCRv6 Small / tr / WASM';

// Verified against PoC and the pinned 0.4.2 package. Dynamic import stays native:
// the SDK, OpenCV, ONNX Runtime and model archives download only on first use.
const importSdk = () => import(/* webpackIgnore: true */ SDK_URL);

function withTimeout(work, ms, lateCleanup) {
  let timer, expired = false;
  const operation = Promise.resolve().then(work).then(value => {
    if (expired) { void lateCleanup?.(value); throw new Error('OCR hazırlığı zaman aşımına uğradı.'); }
    return value;
  });
  return Promise.race([operation, new Promise((_, reject) => {
    timer = setTimeout(() => { expired = true; reject(new Error('OCR hazırlığı zaman aşımına uğradı. Yeniden deneyebilirsin.')); }, ms);
  })]).finally(() => clearTimeout(timer));
}

export function createPaddleRecognizer(loadSdk = importSdk, modelBase = process.env.REACT_APP_DUY_VE_YAZ_MODEL_BASE,
  assetLoader = loadSdk === importSdk ? loadOcrAssets : null, { initTimeoutMs = 90000 } = {}) {
  let model, pending, busy = false, disposed = false;
  let modelCacheStats = [];
  const progressListeners = new Set();
  let progress = { stage: 'library', percent: 0 };
  const reportProgress = value => {
    progress = { ...progress, ...value };
    if (!disposed) for (const listener of progressListeners) listener(progress);
  };
  async function disposeModel() {
    const old = model; model = null;
    if (old) { try { await old.dispose(); } catch (_) { /* best-effort WASM cleanup */ } }
  }
  async function load() {
    if (model) return model;
    if (!pending) pending = (async () => {
      reportProgress({ stage: 'library', percent: 0 });
      const { PaddleOCR } = await withTimeout(loadSdk, initTimeoutMs);
      if (disposed) throw new Error('Önizleme kapatıldı.');
      // The production path validates every model against pinned byte size and
      // SHA-256, then reuses Cache Storage on subsequent visits/other levels.
      // Mock SDK injection retains the legacy lightweight unit-test path.
      const cached = assetLoader ? await assetLoader({ modelBase,
        onProgress: value => reportProgress({ ...value, stage: 'models' }) }) : null;
      try {
        if (disposed) throw new Error('Önizleme kapatıldı.');
        reportProgress({ stage: 'initializing', percent: cached ? 100 : null });
        const base = typeof modelBase === 'string' ? modelBase.trim().replace(/\/+$/, '') : '';
        const legacyAssets = !cached && base ? {
          textDetectionModelName: 'PP-OCRv6_small_det',
          textRecognitionModelName: 'PP-OCRv6_small_rec',
          textDetectionModelAsset: { url: `${base}/PP-OCRv6_small_det_onnx_infer.tar` },
          textRecognitionModelAsset: { url: `${base}/PP-OCRv6_small_rec_onnx_infer.tar` },
        } : {};
        model = await withTimeout(() => PaddleOCR.create({ lang: 'tr', ocrVersion: 'PP-OCRv6',
          ...(cached?.options || legacyAssets),
          ortOptions: { backend: 'wasm', numThreads: 1, simd: true } }), initTimeoutMs,
        async late => { try { await late.dispose(); } catch (_) { /* late WASM cleanup */ } });
        modelCacheStats = cached?.stats || [];
      } finally { cached?.release(); }
      if (disposed) { await disposeModel(); throw new Error('Önizleme kapatıldı.'); }
      reportProgress({ stage: 'ready', percent: 100 });
      return model;
    })().finally(() => { pending = null; });
    return pending;
  }
  return {
    // Start SDK, WASM and model downloads as soon as the staff preview mounts.
    // The same shared load() promise is reused by recognize, never fetched twice.
    async warmUp(onPhase, onProgress) {
      if (disposed) throw new Error('Önizleme kapatıldı.');
      const start = performance.now();
      onPhase?.('loading');
      if (onProgress) { progressListeners.add(onProgress); onProgress(progress); }
      try { await load(); } finally { progressListeners.delete(onProgress); }
      if (disposed) throw new Error('Önizleme kapatıldı.');
      onPhase?.('ready');
      return { modelLoadMs: Math.round(performance.now() - start), model: MODEL_NAME, modelCacheStats };
    },
    async recognize(canvas, onPhase) {
      if (disposed) throw new Error('Önizleme kapatıldı.');
      if (busy) throw new Error('Bir tanıma zaten sürüyor.');
      busy = true;
      try {
        const coldStart = !model;
        const loadStarted = performance.now();
        if (!model) onPhase?.('loading');
        const engine = await load();
        const modelLoadMs = coldStart ? Math.round(performance.now() - loadStarted) : null;
        if (disposed) throw new Error('Önizleme kapatıldı.');
        onPhase?.('recognizing');
        // Let the browser paint the status before WASM inference begins.
        await new Promise(resolve => setTimeout(resolve, 32));
        if (disposed) throw new Error('Önizleme kapatıldı.');
        const started = performance.now();
        const [result] = await engine.predict(canvas);
        const items = result?.items || [];
        const scores = items.map(item => item.score);
        return { text: items.map(item => item.text || '').join(' '),
          confidence: scores.length && scores.every(Number.isFinite) ? Math.min(...scores) : null,
          latencyMs: Math.round(performance.now() - started), model: MODEL_NAME, modelVersion: MODEL_VERSION,
          coldStart, modelLoadMs,
          raw: { items, metrics: result?.metrics, runtime: result?.runtime, modelCacheStats } };
      } finally { busy = false; if (disposed) await disposeModel(); }
    },
    dispose() { disposed = true; if (!busy && !pending) return disposeModel(); return Promise.resolve(); },
  };
}
